import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Select, Card, Typography, Space, App, Tag } from 'antd';
import { MergeCellsOutlined, TableOutlined, UserOutlined } from '@ant-design/icons';
import { Table, MergedTableInfo } from '../../../types/tables';
import { useGetTablesQuery, useMergeTablesMutation } from '../../../services/tablesApi';

const { Title, Text } = Typography;

interface MergeTablesModalProps {
  visible: boolean;
  primaryTable?: Table;
  onCancel: () => void;
  /** Called after a successful merge with the primary table and the tables
   * that just joined it (the RTK cache refetch may not have landed yet). */
  onSuccess: (primaryTable: Table, joinedTables: MergedTableInfo[]) => void;
}

const MergeTablesModal: React.FC<MergeTablesModalProps> = ({
  visible,
  primaryTable,
  onCancel,
  onSuccess,
}) => {
  const { message } = App.useApp();
  const [selectedTableIds, setSelectedTableIds] = useState<string[]>([]);

  // All tables, grouped by area in the select — joining tables from another
  // area is unusual but legal (e.g. terrace overflow into the salon).
  const { data: tables = [] } = useGetTablesQuery(undefined, { skip: !visible });

  const [mergeTables, { isLoading: mergeLoading }] = useMergeTablesMutation();

  useEffect(() => {
    if (!visible) {
      setSelectedTableIds([]);
    }
  }, [visible]);

  // Only free tables can join a group; merged secondaries are Occupied so the
  // status filter already excludes them.
  const candidateTables = useMemo(
    () => tables.filter(
      t => (t.status === 'Available' || t.status === 'Reserved') &&
           t.id !== primaryTable?.id &&
           !t.mergedIntoTableId &&
           !(t.mergedTables && t.mergedTables.length > 0)
    ),
    [tables, primaryTable?.id]
  );

  const groupedOptions = useMemo(() => {
    const byArea = new Map<string, typeof candidateTables>();
    for (const table of candidateTables) {
      const group = byArea.get(table.areaName) || [];
      group.push(table);
      byArea.set(table.areaName, group);
    }
    return Array.from(byArea.entries()).map(([areaName, areaTables]) => ({
      label: areaName,
      options: areaTables.map(t => ({
        value: t.id,
        label: `Mesa ${t.number} · ${t.capacity} personas${t.status === 'Reserved' ? ' · Reservada' : ''}`,
      })),
    }));
  }, [candidateTables]);

  const selectedTables = candidateTables.filter(t => selectedTableIds.includes(t.id));
  const combinedCapacity =
    (primaryTable?.capacity ?? 0) + selectedTables.reduce((sum, t) => sum + t.capacity, 0);

  const handleMerge = async () => {
    if (!primaryTable?.id || selectedTableIds.length === 0) {
      message.error('Selecciona al menos una mesa para unir');
      return;
    }

    try {
      await mergeTables({
        primaryTableId: primaryTable.id,
        secondaryTableIds: selectedTableIds,
      }).unwrap();

      message.success(
        `Mesa ${primaryTable.number} unida con ${selectedTables.map(t => t.number).join(', ')}`
      );
      onSuccess(
        primaryTable,
        selectedTables.map(t => ({ id: t.id, number: t.number, capacity: t.capacity }))
      );
    } catch (error: any) {
      console.error('Error al unir mesas:', error);
      message.error(error?.data?.message || 'Error al unir las mesas');
    }
  };

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onCancel}
      onOk={handleMerge}
      confirmLoading={mergeLoading}
      width={500}
      okText="Unir Mesas"
      cancelText="Cancelar"
      style={{ top: 50 }}
      zIndex={1100}
      okButtonProps={{
        disabled: selectedTableIds.length === 0,
        size: 'large',
        icon: <MergeCellsOutlined />,
      }}
      cancelButtonProps={{ size: 'large' }}
    >
      <div style={{ padding: '20px 4px 0' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <Title level={3} style={{ marginBottom: '4px', color: '#1890ff' }}>
            <MergeCellsOutlined style={{ marginRight: '8px' }} />
            Unir Mesas
          </Title>
          <Text type="secondary">
            Las mesas unidas comparten una sola cuenta y no pueden abrirse por separado
          </Text>
        </div>

        {/* Primary Table Info */}
        <Card
          size="small"
          style={{ marginBottom: '16px', backgroundColor: 'var(--bg-info-light)', borderColor: 'var(--border-info)' }}
          styles={{ body: { padding: '12px' } }}
        >
          <Space>
            <TableOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
            <div>
              <Text strong>Mesa Principal: </Text>
              <Tag color="blue">{primaryTable?.number}</Tag>
              <Text type="secondary" style={{ marginLeft: '8px' }}>
                {primaryTable?.areaName} · {primaryTable?.capacity} personas
              </Text>
            </div>
          </Space>
        </Card>

        {/* Secondary Tables Selection */}
        <div style={{ marginBottom: '16px' }}>
          <Text strong style={{ display: 'block', marginBottom: '8px' }}>
            <TableOutlined style={{ marginRight: '8px' }} />
            Mesas a unir
          </Text>
          <Select
            mode="multiple"
            value={selectedTableIds}
            onChange={setSelectedTableIds}
            style={{ width: '100%' }}
            size="large"
            placeholder="Selecciona una o más mesas disponibles"
            options={groupedOptions}
            optionFilterProp="label"
            notFoundContent="No hay mesas disponibles para unir"
          />
        </div>

        {/* Combined capacity summary */}
        {selectedTables.length > 0 && (
          <Card
            size="small"
            style={{ backgroundColor: 'var(--bg-success-light)', borderColor: 'var(--border-success)' }}
            styles={{ body: { padding: '12px' } }}
          >
            <Space>
              <UserOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
              <div>
                <Text strong>Capacidad combinada: </Text>
                <Tag color="green">{combinedCapacity} personas</Tag>
                <Text type="secondary" style={{ marginLeft: '8px' }}>
                  Mesa {primaryTable?.number} + {selectedTables.map(t => t.number).join(', ')}
                </Text>
              </div>
            </Space>
          </Card>
        )}

        {/* Info Note */}
        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: 'var(--bg-info-light)', borderRadius: '8px' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            <strong>Nota:</strong> La cuenta se maneja desde la mesa principal. Las mesas unidas
            se liberan automáticamente al cobrar, o manualmente con "Separar Mesas".
          </Text>
        </div>
      </div>
    </Modal>
  );
};

export default MergeTablesModal;
